import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Bell, Smartphone, Mail, Save, Loader2, Package, ShoppingCart, Users, Scissors } from 'lucide-react';
import { useDashboardTheme } from '../context/DashboardThemeContext';
import { supabase } from '../lib/supabase';
import { cn } from '@/lib/utils';

const NOTIF_KEYS = {
  channel_phone: 'channel_phone',
  channel_email: 'channel_email',
  new_order: 'new_order',
  low_stock: 'low_stock',
  out_of_stock: 'out_of_stock',
  payment_received: 'payment_received',
  order_shipped: 'order_shipped',
  contact_message: 'contact_message',
  newsletter_signup: 'newsletter_signup',
  new_review: 'new_review',
  abandoned_cart: 'abandoned_cart',
  custom_order: 'custom_order',
  back_in_stock: 'back_in_stock',
  order_cancelled: 'order_cancelled',
  critical_stock: 'critical_stock',
  new_customer: 'new_customer',
  discount_used: 'discount_used',
  refund_requested: 'refund_requested',
} as const;

type NotifKey = keyof typeof NOTIF_KEYS;

const CATEGORIES: {
  id: string;
  title: string;
  icon: typeof Package;
  items: { key: NotifKey; label: string; description?: string }[];
}[] = [
  {
    id: 'orders',
    title: 'Commandes',
    icon: Package,
    items: [
      { key: 'new_order', label: 'Nouvelles commandes', description: 'Commande passée' },
      { key: 'payment_received', label: 'Paiement reçu', description: 'Confirmation de paiement' },
      { key: 'order_shipped', label: 'Commande expédiée', description: 'Envoi du colis' },
      { key: 'order_cancelled', label: 'Commande annulée' },
    ],
  },
  {
    id: 'stock',
    title: 'Stock',
    icon: Package,
    items: [
      { key: 'low_stock', label: 'Stock faible', description: 'Sous le seuil d\'alerte' },
      { key: 'out_of_stock', label: 'Rupture de stock' },
      { key: 'critical_stock', label: 'Alerte stock critique' },
      { key: 'back_in_stock', label: 'Produit de retour en stock' },
    ],
  },
  {
    id: 'clients',
    title: 'Clients & messages',
    icon: Users,
    items: [
      { key: 'contact_message', label: 'Nouveau message contact' },
      { key: 'newsletter_signup', label: 'Inscription newsletter' },
      { key: 'new_review', label: 'Nouvel avis client' },
      { key: 'new_customer', label: 'Nouveau client inscrit' },
    ],
  },
  {
    id: 'sales',
    title: 'Ventes & panier',
    icon: ShoppingCart,
    items: [
      { key: 'abandoned_cart', label: 'Panier abandonné' },
      { key: 'discount_used', label: 'Code promo utilisé' },
    ],
  },
  {
    id: 'bespoke',
    title: 'Sur-mesure & autres',
    icon: Scissors,
    items: [
      { key: 'custom_order', label: 'Demande sur-mesure', description: 'Nouvelle demande atelier' },
      { key: 'refund_requested', label: 'Demande de remboursement' },
    ],
  },
];

function Switch({
  checked,
  onCheckedChange,
  disabled,
  isLight,
  ariaLabel,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  isLight: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked ? 'true' : 'false'}
      aria-label={ariaLabel ?? (checked ? 'Activé' : 'Désactivé')}
      title={ariaLabel ?? (checked ? 'Activé' : 'Désactivé')}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        isLight ? 'focus-visible:ring-neutral-400' : 'focus-visible:ring-white/30',
        checked
          ? isLight
            ? 'bg-neutral-900'
            : 'bg-white'
          : isLight
            ? 'bg-neutral-200'
            : 'bg-white/20'
      )}
    >
      <span
        className={cn(
          'pointer-events-none block h-5 w-5 rounded-full shadow-md transition-transform rtl:translate-x-0 mt-0.5',
          checked ? 'translate-x-6 ml-0.5' : 'translate-x-0.5',
          isLight ? 'bg-white' : 'bg-neutral-900'
        )}
      />
    </button>
  );
}

const TABLE_NAME = 'notification_settings';

export function DashboardNotifications() {
  const { theme } = useDashboardTheme();
  const isLight = theme === 'light';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const keys = Object.values(NOTIF_KEYS);
    supabase
      .from(TABLE_NAME)
      .select('key, value')
      .in('key', keys)
      .then(({ data }) => {
        const next: Record<string, boolean> = {};
        keys.forEach((k) => {
          next[k] = false;
        });
        (data ?? []).forEach((row: { key: string; value: string | null }) => {
          next[row.key] = row.value === 'true';
        });
        setValues(next);
        setLoading(false);
      });
  }, []);

  const set = (key: string, v: boolean) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  const save = async () => {
    setSaving(true);
    const entries = Object.entries(values);
    for (const [key, val] of entries) {
      await supabase.from(TABLE_NAME).upsert(
        { key, value: val ? 'true' : 'false', updated_at: new Date().toISOString() },
        { onConflict: 'key' }
      );
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex flex-1 flex-col gap-6">
        <div className="flex justify-center py-12">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6">
      <div
        className={cn(
          'rounded-3xl border p-6 md:p-8',
          isLight ? 'border-border/80 bg-card shadow-[0px_20px_40px_rgba(0,0,0,0.06)]' : 'border-white/10 bg-white/5'
        )}
      >
        <p className="flex items-center gap-2 font-['Rubik'] text-sm font-medium text-muted-foreground">
          <Bell className="size-4" />
          Notifications
        </p>
        <h2 className="mt-1 font-['Sora'] text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          Paramètres de notifications
        </h2>
        <p className="mt-1 font-['Rubik'] text-sm text-muted-foreground">
          Choisissez où et quelles notifications recevoir.
        </p>
      </div>

      {/* Canaux : Téléphone et Email côte à côte */}
      <Card
        className={cn(
          'overflow-hidden rounded-2xl',
          isLight ? 'border-border/80 bg-card shadow-[0px_20px_40px_rgba(0,0,0,0.06)]' : 'border-white/10 bg-white/5'
        )}
      >
        <CardHeader className={isLight ? 'border-b border-border/60' : 'border-b border-white/10'}>
          <CardTitle className="text-foreground">Canaux</CardTitle>
          <CardDescription className="text-muted-foreground">
            Où souhaitez-vous recevoir les notifications ?
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              className={cn(
                'flex items-center justify-between gap-4 rounded-xl border p-4',
                isLight ? 'border-border/60' : 'border-white/10'
              )}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted/50">
                  <Smartphone className="size-5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <Label className="text-base font-medium text-foreground">Téléphone</Label>
                  <p className="text-sm text-muted-foreground truncate">SMS / push</p>
                </div>
              </div>
              <Switch
                checked={values[NOTIF_KEYS.channel_phone] ?? false}
                onCheckedChange={(v) => set(NOTIF_KEYS.channel_phone, v)}
                isLight={isLight}
                ariaLabel="Notifications par téléphone"
              />
            </div>
            <div
              className={cn(
                'flex items-center justify-between gap-4 rounded-xl border p-4',
                isLight ? 'border-border/60' : 'border-white/10'
              )}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted/50">
                  <Mail className="size-5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <Label className="text-base font-medium text-foreground">Email</Label>
                  <p className="text-sm text-muted-foreground truncate">Par email</p>
                </div>
              </div>
              <Switch
                checked={values[NOTIF_KEYS.channel_email] ?? false}
                onCheckedChange={(v) => set(NOTIF_KEYS.channel_email, v)}
                isLight={isLight}
                ariaLabel="Notifications par email"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Types groupés par catégorie */}
      <Card
        className={cn(
          'overflow-hidden rounded-2xl',
          isLight ? 'border-border/80 bg-card shadow-[0px_20px_40px_rgba(0,0,0,0.06)]' : 'border-white/10 bg-white/5'
        )}
      >
        <CardHeader className={isLight ? 'border-b border-border/60' : 'border-b border-white/10'}>
          <CardTitle className="text-foreground">Types de notifications</CardTitle>
          <CardDescription className="text-muted-foreground">
            Sélectionnez les événements pour lesquels vous voulez être notifié.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-8">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <section key={cat.id}>
                <h3 className="flex items-center gap-2 font-['Sora'] text-sm font-semibold uppercase tracking-wider text-foreground mb-3">
                  <Icon className="size-4 text-muted-foreground" />
                  {cat.title}
                </h3>
                <ul className="space-y-2">
                  {cat.items.map(({ key, label, description }) => (
                    <li
                      key={key}
                      className={cn(
                        'flex items-center justify-between gap-4 rounded-xl border px-4 py-3',
                        isLight ? 'border-border/60' : 'border-white/10'
                      )}
                    >
                      <div>
                        <p className="font-['Rubik'] text-sm font-medium text-foreground">{label}</p>
                        {description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
                        )}
                      </div>
                      <Switch
                        checked={values[NOTIF_KEYS[key]] ?? false}
                        onCheckedChange={(v) => set(NOTIF_KEYS[key], v)}
                        isLight={isLight}
                        ariaLabel={label}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
          <div className="pt-2">
            <Button className="w-fit gap-2 rounded-xl" size="sm" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
